import { useQuery } from '@tanstack/react-query'

import { useApi } from '@fortem-monorepo/react-spa'
import { Trades } from '@fortem-monorepo/web-sdk'

import { useCurrencies } from '@/react-query/queries/currencies'

export const useTradeStrategiesQuery = () => {
  const api = useApi()

  return useQuery({
    queryKey: ['static', 'trade-strategies'],
    queryFn: () => api.misc.getStrategies(),
  })
}

export const useTradersQuery = (params: Trades.GetTradersParams = {}) => {
  const api = useApi()

  return useQuery({
    queryKey: ['static', 'traders', params],
    queryFn: () => api.trades.getTraders(params),
  })
}

export const useTradeTypesQuery = () => {
  const api = useApi()

  return useQuery({
    queryKey: ['static', 'trade-types'],
    queryFn: () => api.trades.getTradeTypes(),
  })
}

export const useTopLevelBucketsQuery = () => {
  const api = useApi()

  return useQuery({
    queryKey: ['static', 'buckets', 'top-level'],
    queryFn: () => api.buckets.getTopLevelBuckets(),
  })
}
export const useInvestmentBucketsQuery = () => {
  const api = useApi()

  return useQuery({
    queryKey: ['static', 'buckets', 'investment'],
    queryFn: () => api.buckets.getInvestmentBuckets(),
  })
}
export const useWrapperBucketsQuery = () => {
  const api = useApi()

  return useQuery({
    queryKey: ['static', 'buckets', 'wrapper'],
    queryFn: () => api.buckets.getWrapperBuckets(),
  })
}

export const useAgentsQuery = () => {
  const api = useApi()

  return useQuery({
    queryKey: ['static', 'prime-brokers'],
    queryFn: () => api.misc.getPrimeBrokers(),
  })
}

export const useTradeClientsQuery = () => {
  const api = useApi()

  return useQuery({
    queryKey: ['static', 'trade-clients'],
    queryFn: () => api.misc.getTradeClients(),
  })
}

export const useTradeCounterpartiesQuery = () => {
  const api = useApi()

  return useQuery({
    queryKey: ['static', 'trade-counterparties'],
    queryFn: () => api.misc.getCounterparties(),
  })
}

export const useAllTradersQuery = () => {
  const api = useApi()

  return useQuery({
    queryKey: ['static', 'traders', 'all'],
    queryFn: () => api.misc.getAllTraders(),
  })
}

export const useTradingData = () => {
  // Currencies
  const currencies = useCurrencies()
  // Counterparties
  const counterparties = useTradeCounterpartiesQuery()
  // Strategies
  const strategies = useTradeStrategiesQuery()
  // Clients
  const clients = useTradeClientsQuery()
  // Traders
  const traders = useAllTradersQuery()
  // Top Level Buckets
  const topLevelBuckets = useTopLevelBucketsQuery()
  // Investment Buckets
  const investmentBuckets = useInvestmentBucketsQuery()
  // Wrapper Buckets
  const wrapperBuckets = useWrapperBucketsQuery()

  const maps = {
    currencies: new Map((currencies.data ?? []).map((c) => [c.value, c.label])),
    strategies: new Map((strategies.data ?? []).map((s) => [s.strategy_id, s.strategy_name])),
    counterparties: new Map((counterparties.data ?? []).map((c) => [c.value, c.label])),
    clients: new Map((clients.data ?? []).map((c) => [c.value, c.label])),
    traders: new Map((traders.data ?? []).map((t) => [t.trader_id, t.full_name])),
    topLevelBuckets: new Map(
      (topLevelBuckets.data ?? []).filter((v) => v.value).map((b) => [b.value, b.label]),
    ),
    investmentBuckets: new Map(
      (investmentBuckets.data ?? []).filter((v) => v.value).map((b) => [b.value, b.label]),
    ),
    wrapperBuckets: new Map(
      (wrapperBuckets.data ?? []).filter((v) => v.value).map((b) => [b.value, b.label]),
    ),
  }

  return {
    currencies,
    counterparties,
    strategies,
    clients,
    traders,
    topLevelBuckets,
    investmentBuckets,
    wrapperBuckets,
    maps,
  }
}
